import { Maybe } from '@codegrade/maybe';
/** A function that doesn't do anything. */
// eslint-disable-next-line @typescript-eslint/no-empty-function
export function noop() { }
/**
 * Debounce the given function.
 *
 * This returns a new function that wraps the input function. When the wrapping function is called,
 * it will not call the wrapped function immediately, but instead only after the given `delay`.
 * However, if the wrapping function is called again within that `delay`, the previous call will
 * be cancelled, and a new timer is set to call the function after the delay.
 *
 * @param fn The function to debounce.
 * @param delay The debounce delay.
 * @returns A debounced version of the input function, that accepts the same arguments.
 */
export function debounce(fn, delay) {
    let timer = Maybe.empty();
    return (...args) => {
        timer.ifJust(clearTimeout);
        timer = Maybe.of(setTimeout(fn, delay.asMilliseconds(), ...args));
    };
}
const DEBOUNCED = Object.freeze({ tag: 'debounced' });
/**
 * Debounce the given function with a result.
 *
 * Similar to {@link debounce}, this returns a new debounced function. `debounceWithResult` returns
 * a promise that either contains the given function's return value (when the function is called
 * after the debounce delay), or a object that signals the call to the function is rescheduled.
 *
 * @param fn The function to debounce.
 * @param delay The debounce delay.
 * @returns A debounced version of the input function, that accepts the same arguments.
 */
export function debounceWithResult(fn, delay) {
    let clearPrevious = noop;
    return {
        func: (...args) => new Promise((resolve, reject) => {
            // Cancel the previous setTimeout
            clearPrevious();
            // Schedule a new call after a delay.
            const timer = setTimeout(() => {
                clearPrevious = noop;
                fn(...args).then(value => resolve({ tag: 'result', value }), reject);
            }, delay.asMilliseconds());
            /** Stores a new function to cancel this new schedule. */
            clearPrevious = () => {
                resolve(DEBOUNCED);
                clearTimeout(timer);
                clearPrevious = noop;
            };
        }),
        cancel: () => clearPrevious(),
    };
}
